import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApplicationReviewSection = ({ formData, documents, skills, onEdit }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const reviewSections = [
    {
      id: 'personal',
      title: 'Personal Information',
      icon: 'User',
      items: [
        { label: 'Full Name', value: formData?.fullName },
        { label: 'Email', value: formData?.email },
        { label: 'Phone', value: formData?.phone },
        { label: 'Location', value: formData?.location },
        { label: 'LinkedIn', value: formData?.linkedin || 'Not provided' },
        { label: 'Portfolio', value: formData?.portfolio || 'Not provided' }
      ]
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: 'FileText',
      items: Object.entries(documents)?.map(([key, file]) => ({
        label: key?.charAt(0)?.toUpperCase() + key?.slice(1)?.replace(/([A-Z])/g, ' $1'),
        value: file ? `${file?.name} (${formatFileSize(file?.size)})` : 'Not uploaded'
      }))
    },
    {
      id: 'questions',
      title: 'Application Details',
      icon: 'MessageSquare',
      items: [
        { label: 'Experience', value: formData?.experience },
        { label: 'Current CTC', value: `₹${formData?.currentCtc}` },
        { label: 'Expected CTC', value: `₹${formData?.expectedCtc}` },
        { label: 'Notice Period', value: formData?.noticePeriod },
        { label: 'Willing to Relocate', value: formData?.willingToRelocate ? 'Yes' : 'No' },
        { label: 'Open to Remote', value: formData?.openToRemote ? 'Yes' : 'No' },
        { label: 'Why Interested', value: formData?.whyInterested },
        { label: 'Relevant Skills', value: formData?.relevantSkills || 'Not provided' }
      ]
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Review Your Application</h3>
          <p className="text-sm text-muted-foreground">Please verify all information before submitting</p>
        </div>
      </div>
      <div className="space-y-6">
        {reviewSections?.map((section) => (
          <div key={section?.id} className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name={section?.icon} size={18} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">{section?.title}</h4>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(section?.id)}
                iconName="Edit2"
                iconPosition="left"
                iconSize={14}
              >
                Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section?.items?.map((item, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{item?.label}</p>
                  <p className="text-sm text-foreground break-words">{item?.value}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {skills?.length > 0 && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="Target" size={18} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Skills Assessment</h4>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit('skills')}
                iconName="Edit2"
                iconPosition="left"
                iconSize={14}
              >
                Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {skills?.map((skill) => (
                <div key={skill?.id} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{skill?.name}</span>
                  <div className="flex items-center gap-2">
                    {[...Array(skill?.rating)]?.map((_, i) => (
                      <Icon key={i} name="Star" size={14} className="text-primary fill-current" />
                    ))}
                    {[...Array(4 - skill?.rating)]?.map((_, i) => (
                      <Icon key={i} name="Star" size={14} className="text-muted-foreground" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationReviewSection;